/**
 * Visual CMS Page Editor - Olympic Fit Wellness
 * Drag-and-drop page builder with block-based editing
 */
import { useState, useCallback } from 'react';
import { 
  Type, 
  Image, 
  Columns, 
  List, 
  Square, 
  Video, 
  Link, 
  X, 
  GripVertical, 
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Settings,
  Eye
} from 'lucide-react';

// Block type definitions
const BLOCK_TYPES = {
  heading: { icon: Type, label: 'Heading', defaultContent: { text: 'New Heading', level: 'h2' } },
  paragraph: { icon: Type, label: 'Paragraph', defaultContent: { text: 'Enter your text here...' } },
  image: { icon: Image, label: 'Image', defaultContent: { src: '', alt: '', caption: '' } },
  button: { icon: Square, label: 'Button', defaultContent: { text: 'Click Me', link: '#', style: 'primary' } },
  columns: { icon: Columns, label: '2 Columns', defaultContent: { left: [], right: [] } },
  list: { icon: List, label: 'List', defaultContent: { items: ['Item 1', 'Item 2', 'Item 3'], style: 'bullet' } },
  video: { icon: Video, label: 'Video', defaultContent: { url: '', autoplay: false } },
  spacer: { icon: Square, label: 'Spacer', defaultContent: { height: 40 } },
  divider: { icon: Square, label: 'Divider', defaultContent: { style: 'solid' } },
  cta: { icon: Link, label: 'Call to Action', defaultContent: { title: 'Ready to Start?', subtitle: 'Join us today', buttonText: 'Get Started', buttonLink: '/membership' } }
};

// Block Editor Component
function BlockEditor({ block, onChange, onDelete }) {
  const [isExpanded, setIsExpanded] = useState(true);
  
  const updateContent = (key, value) => {
    onChange({ ...block, content: { ...block.content, [key]: value } });
  };

  const renderEditor = () => {
    switch (block.type) {
      case 'heading':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-neutral-400 mb-1">Text</label>
              <input
                type="text"
                value={block.content.text || ''}
                onChange={(e) => updateContent('text', e.target.value)}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-400 mb-1">Level</label>
              <select
                value={block.content.level || 'h2'}
                onChange={(e) => updateContent('level', e.target.value)}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded text-white text-sm"
              >
                <option value="h1">H1 - Main Title</option>
                <option value="h2">H2 - Section Title</option>
                <option value="h3">H3 - Subsection</option>
                <option value="h4">H4 - Small Heading</option>
              </select>
            </div>
          </div>
        );
      
      case 'paragraph':
        return (
          <div>
            <label className="block text-xs font-medium text-neutral-400 mb-1">Text</label>
            <textarea
              value={block.content.text || ''}
              onChange={(e) => updateContent('text', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded text-white text-sm resize-none"
            />
          </div>
        );
      
      case 'image':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-neutral-400 mb-1">Image URL</label>
              <input
                type="url"
                value={block.content.src || ''}
                onChange={(e) => updateContent('src', e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-400 mb-1">Alt Text</label>
              <input
                type="text"
                value={block.content.alt || ''}
                onChange={(e) => updateContent('alt', e.target.value)}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-400 mb-1">Caption (optional)</label>
              <input
                type="text"
                value={block.content.caption || ''}
                onChange={(e) => updateContent('caption', e.target.value)}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded text-white text-sm"
              />
            </div>
            {block.content.src && (
              <img src={block.content.src} alt={block.content.alt} className="w-full h-32 object-cover rounded" />
            )}
          </div>
        );
      
      case 'button':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-neutral-400 mb-1">Button Text</label>
              <input
                type="text"
                value={block.content.text || ''}
                onChange={(e) => updateContent('text', e.target.value)}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-400 mb-1">Link</label>
              <input
                type="text"
                value={block.content.link || ''}
                onChange={(e) => updateContent('link', e.target.value)}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-400 mb-1">Style</label>
              <select
                value={block.content.style || 'primary'}
                onChange={(e) => updateContent('style', e.target.value)}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded text-white text-sm"
              >
                <option value="primary">Primary (Blue)</option>
                <option value="secondary">Secondary (White)</option>
                <option value="outline">Outline</option>
              </select>
            </div>
          </div>
        );
      
      case 'list':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-neutral-400 mb-1">Style</label>
              <select
                value={block.content.style || 'bullet'}
                onChange={(e) => updateContent('style', e.target.value)}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded text-white text-sm"
              >
                <option value="bullet">Bullet Points</option>
                <option value="numbered">Numbered</option>
                <option value="check">Checkmarks</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-400 mb-1">Items (one per line)</label>
              <textarea
                value={(block.content.items || []).join('\n')}
                onChange={(e) => updateContent('items', e.target.value.split('\n'))}
                rows={4}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded text-white text-sm resize-none"
              />
            </div>
          </div>
        );
      
      case 'spacer':
        return (
          <div>
            <label className="block text-xs font-medium text-neutral-400 mb-1">Height (px)</label>
            <input
              type="number"
              value={block.content.height || 40}
              onChange={(e) => updateContent('height', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded text-white text-sm"
            />
          </div>
        );
      
      case 'cta':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-neutral-400 mb-1">Title</label>
              <input
                type="text"
                value={block.content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-400 mb-1">Subtitle</label>
              <input
                type="text"
                value={block.content.subtitle || ''}
                onChange={(e) => updateContent('subtitle', e.target.value)}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-400 mb-1">Button Text</label>
              <input
                type="text"
                value={block.content.buttonText || ''}
                onChange={(e) => updateContent('buttonText', e.target.value)}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-400 mb-1">Button Link</label>
              <input
                type="text"
                value={block.content.buttonLink || ''}
                onChange={(e) => updateContent('buttonLink', e.target.value)}
                className="w-full px-3 py-2 bg-neutral-700 border border-neutral-600 rounded text-white text-sm"
              />
            </div>
          </div>
        );
      
      default:
        return <p className="text-neutral-400 text-sm">No editor available for this block type.</p>;
    }
  };

  const BlockIcon = BLOCK_TYPES[block.type]?.icon || Square;

  return (
    <div className="bg-neutral-800 rounded-lg border border-neutral-700 overflow-hidden">
      <div 
        className="flex items-center gap-3 p-3 bg-neutral-700/50 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <GripVertical className="w-4 h-4 text-neutral-500 cursor-grab" />
        <BlockIcon className="w-4 h-4 text-[#3B82F6]" />
        <span className="text-sm font-medium text-white flex-1">
          {BLOCK_TYPES[block.type]?.label || block.type}
        </span>
        <button 
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="p-1 hover:bg-red-500/20 rounded"
        >
          <Trash2 className="w-4 h-4 text-red-400" />
        </button>
        {isExpanded ? <ChevronUp className="w-4 h-4 text-neutral-400" /> : <ChevronDown className="w-4 h-4 text-neutral-400" />}
      </div>
      {isExpanded && (
        <div className="p-4 border-t border-neutral-700">
          {renderEditor()}
        </div>
      )}
    </div>
  );
}

// Block Preview Component
function BlockPreview({ block }) {
  switch (block.type) {
    case 'heading':
      const HeadingTag = block.content.level || 'h2';
      const headingClasses = {
        h1: 'text-4xl font-bold',
        h2: 'text-3xl font-bold',
        h3: 'text-2xl font-semibold',
        h4: 'text-xl font-semibold'
      };
      return <HeadingTag className={`${headingClasses[HeadingTag]} text-white`}>{block.content.text}</HeadingTag>;
    
    case 'paragraph':
      return <p className="text-neutral-300">{block.content.text}</p>;
    
    case 'image':
      return block.content.src ? (
        <div>
          <img src={block.content.src} alt={block.content.alt} className="w-full rounded-lg" />
          {block.content.caption && <p className="text-sm text-neutral-400 mt-2 text-center">{block.content.caption}</p>}
        </div>
      ) : (
        <div className="bg-neutral-700 h-48 rounded-lg flex items-center justify-center">
          <Image className="w-12 h-12 text-neutral-500" />
        </div>
      );
    
    case 'button':
      const buttonClasses = {
        primary: 'bg-[#3B82F6] text-white hover:bg-blue-600',
        secondary: 'bg-white text-black hover:bg-neutral-100',
        outline: 'border-2 border-white text-white hover:bg-white/10'
      };
      return (
        <button className={`px-6 py-3 font-semibold rounded ${buttonClasses[block.content.style] || buttonClasses.primary}`}>
          {block.content.text}
        </button>
      );
    
    case 'list':
      const ListTag = block.content.style === 'numbered' ? 'ol' : 'ul';
      return (
        <ListTag className={`space-y-2 ${block.content.style === 'numbered' ? 'list-decimal' : 'list-disc'} list-inside text-neutral-300`}>
          {(block.content.items || []).map((item, i) => <li key={i}>{item}</li>)}
        </ListTag>
      );
    
    case 'spacer':
      return <div style={{ height: block.content.height || 40 }} />;
    
    case 'divider':
      return <hr className="border-neutral-600" />;
    
    case 'cta':
      return (
        <div className="bg-gradient-to-r from-[#3B82F6] to-blue-700 rounded-xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-2">{block.content.title}</h3>
          <p className="text-blue-100 mb-4">{block.content.subtitle}</p>
          <button className="bg-white text-blue-600 px-6 py-3 font-semibold rounded hover:bg-blue-50">
            {block.content.buttonText}
          </button>
        </div>
      );
    
    default:
      return <div className="bg-neutral-700 p-4 rounded">Unknown block type: {block.type}</div>;
  }
}

// Main Visual Editor Component
export default function VisualCMSEditor({ blocks = [], onChange, onClose, pageTitle }) {
  const [activeView, setActiveView] = useState('edit'); // 'edit' or 'preview'

  const addBlock = useCallback((type) => {
    const newBlock = {
      id: `block_${Date.now()}`,
      type,
      content: { ...BLOCK_TYPES[type].defaultContent }
    };
    onChange([...blocks, newBlock]);
  }, [blocks, onChange]);

  const updateBlock = useCallback((index, updatedBlock) => {
    const newBlocks = [...blocks];
    newBlocks[index] = updatedBlock;
    onChange(newBlocks);
  }, [blocks, onChange]);

  const deleteBlock = useCallback((index) => {
    const newBlocks = blocks.filter((_, i) => i !== index);
    onChange(newBlocks);
  }, [blocks, onChange]);

  const moveBlock = useCallback((index, direction) => {
    const newBlocks = [...blocks];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newBlocks.length) return;
    [newBlocks[index], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[index]];
    onChange(newBlocks);
  }, [blocks, onChange]);

  return (
    <div className="fixed inset-0 z-50 bg-neutral-900 flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-neutral-700 bg-neutral-800">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="p-2 hover:bg-neutral-700 rounded-lg">
            <X className="w-5 h-5 text-neutral-400" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white">Visual Editor</h1>
            <p className="text-sm text-neutral-400">{pageTitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="bg-neutral-700 rounded-lg p-1 flex">
            <button 
              onClick={() => setActiveView('edit')}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${activeView === 'edit' ? 'bg-[#3B82F6] text-white' : 'text-neutral-400 hover:text-white'}`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Edit
            </button>
            <button 
              onClick={() => setActiveView('preview')}
              className={`px-4 py-2 rounded text-sm font-medium transition-colors ${activeView === 'preview' ? 'bg-[#3B82F6] text-white' : 'text-neutral-400 hover:text-white'}`}
            >
              <Eye className="w-4 h-4 inline mr-2" />
              Preview
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex">
        {activeView === 'edit' ? (
          <>
            {/* Block Library */}
            <aside className="w-64 bg-neutral-800 border-r border-neutral-700 p-4 overflow-y-auto">
              <h2 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-4">Add Blocks</h2>
              <div className="space-y-2">
                {Object.entries(BLOCK_TYPES).map(([type, config]) => (
                  <button
                    key={type}
                    onClick={() => addBlock(type)}
                    className="w-full flex items-center gap-3 p-3 bg-neutral-700/50 hover:bg-neutral-700 rounded-lg transition-colors text-left"
                  >
                    <config.icon className="w-5 h-5 text-[#3B82F6]" />
                    <span className="text-sm text-white">{config.label}</span>
                    <Plus className="w-4 h-4 text-neutral-400 ml-auto" />
                  </button>
                ))}
              </div>
            </aside>

            {/* Block Editor */}
            <main className="flex-1 overflow-y-auto p-6">
              <div className="max-w-2xl mx-auto space-y-4">
                {blocks.length === 0 ? (
                  <div className="text-center py-16 border-2 border-dashed border-neutral-700 rounded-xl">
                    <Type className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-neutral-400 mb-2">No content blocks yet</h3>
                    <p className="text-sm text-neutral-500">Click on a block type from the sidebar to add content</p>
                  </div>
                ) : (
                  blocks.map((block, index) => (
                    <div key={block.id} className="group relative">
                      {/* Move buttons */}
                      <div className="absolute -left-12 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1">
                        <button 
                          onClick={() => moveBlock(index, -1)}
                          disabled={index === 0}
                          className="p-1 bg-neutral-700 rounded hover:bg-neutral-600 disabled:opacity-30"
                        >
                          <ChevronUp className="w-4 h-4 text-white" />
                        </button>
                        <button 
                          onClick={() => moveBlock(index, 1)}
                          disabled={index === blocks.length - 1}
                          className="p-1 bg-neutral-700 rounded hover:bg-neutral-600 disabled:opacity-30"
                        >
                          <ChevronDown className="w-4 h-4 text-white" />
                        </button>
                      </div>
                      <BlockEditor
                        block={block}
                        onChange={(updated) => updateBlock(index, updated)}
                        onDelete={() => deleteBlock(index)}
                      />
                    </div>
                  ))
                )}
              </div>
            </main>
          </>
        ) : (
          /* Preview */
          <main className="flex-1 overflow-y-auto bg-white">
            <div className="max-w-4xl mx-auto py-12 px-8 space-y-6">
              {blocks.length === 0 ? (
                <p className="text-center text-gray-400 py-16">No content to preview</p>
              ) : (
                blocks.map((block) => (
                  <div key={block.id} className="preview-block">
                    <BlockPreview block={block} />
                  </div>
                ))
              )}
            </div>
          </main>
        )}
      </div>
    </div>
  );
}
